import * as React from "react";
import { Alert, Box, Button, CircularProgress, Grid, Link, Stack, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useNavigate, useParams } from "react-router-dom";

import {
	downloadBriefDocument,
	useCampaignBrief,
	useDeleteCampaignBriefDraft,
	useUploadBriefPaperwork,
	useWithdrawCampaignBrief,
} from "@/hooks/api/use-campaign-briefs";
import { useDialog } from "@/hooks/use-dialog";
import { RouterLink } from "@/components/core/link";
import { toast } from "@/components/core/toaster";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

import { BriefFiles, BriefSummary, BriefTimeline, StatusChip } from "../components";
import { MAX_FILE_BYTES } from "../constants";

/** One request, as the client who raised it sees it. */
export function CampaignBriefView(): React.JSX.Element {
	const { briefId } = useParams<{ briefId: string }>();
	const navigate = useNavigate();
	const { data: detail, isLoading, isError } = useCampaignBrief(briefId);
	const upload = useUploadBriefPaperwork(briefId ?? "");
	const withdraw = useWithdrawCampaignBrief();
	const discard = useDeleteCampaignBriefDraft();
	const confirmWithdraw = useDialog();
	const confirmDiscard = useDialog();

	if (isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (isError || !detail) {
		return (
			<Box sx={{ p: "var(--Content-padding)" }}>
				<Alert severity="error">This request could not be found.</Alert>
			</Box>
		);
	}

	const { brief } = detail;

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack spacing={3}>
					<Link
						color="text.primary"
						component={RouterLink}
						href="/campaign-briefs"
						sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
						variant="subtitle2"
					>
						<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
						Campaign requests
					</Link>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
						<Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: "1 1 auto" }}>
							<Typography variant="h4">{brief.title}</Typography>
							<StatusChip status={brief.status} />
						</Stack>
						{brief.status === "submitted" ? (
							<Button color="error" variant="outlined" onClick={() => confirmWithdraw.handleOpen()}>
								Withdraw request
							</Button>
						) : null}
						{brief.status === "draft" ? (
							<Button color="error" variant="outlined" onClick={() => confirmDiscard.handleOpen()}>
								Delete draft
							</Button>
						) : null}
					</Stack>
					{brief.status === "draft" ? (
						<Alert severity="warning">
							This request was saved but never submitted, so we haven’t received it. Delete it and start a new one.
						</Alert>
					) : null}
				</Stack>

				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 8 }}>
						<Stack spacing={3}>
							<BriefSummary detail={detail} />
							<BriefFiles
								brief={brief}
								documents={detail.documents}
								uploading={upload.isPending}
								onDownload={(document) =>
									downloadBriefDocument(
										`/campaign-briefs/${brief.id}/documents/${document.id}/download`,
										document.originalFilename
									)
								}
								onUpload={(kind, file) => {
									if (file.size > MAX_FILE_BYTES) {
										toast.error("Files can be at most 25 MB");
										return;
									}
									upload.mutate({ kind, file });
								}}
							/>
						</Stack>
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<BriefTimeline detail={detail} />
					</Grid>
				</Grid>
			</Stack>

			<DeleteModal
				open={confirmWithdraw.open}
				onClose={confirmWithdraw.handleClose}
				title="Withdraw this request?"
				description="We'll stop working on it. To run it later, raise a new request."
				warningText="A withdrawn request can't be reopened."
				deleteText="Withdraw"
				onDelete={() => withdraw.mutateAsync(brief.id)}
			/>
			<DeleteModal
				open={confirmDiscard.open}
				onClose={confirmDiscard.handleClose}
				title="Delete this draft?"
				description="The draft and its files are removed."
				onDelete={async () => {
					await discard.mutateAsync(brief.id);
					navigate("/campaign-briefs");
				}}
			/>
		</Box>
	);
}
